import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type PointerEvent,
	type RefObject,
	type Dispatch,
	type SetStateAction,
} from 'react';
import { useResizeObserver } from 'hooks/useDimensions';
import { graphBounds } from './graphLayout';
import type { GraphPosition, GraphPositions } from './types';

export function useGraphCanvas(
	positions: GraphPositions,
	onMove?: (id: string, position: GraphPosition) => void,
): {
	svg: RefObject<SVGSVGElement>;
	container: RefObject<HTMLDivElement>;
	bounds: ReturnType<typeof graphBounds>;
	scale: number;
	setZoom: Dispatch<SetStateAction<number | undefined>>;
	begin: (id: string, event: PointerEvent<SVGGElement>) => void;
	move: (id: string, event: PointerEvent<SVGGElement>) => void;
	end: () => void;
} {
	const svg = useRef<SVGSVGElement>(null);
	const container = useRef<HTMLDivElement>(null);
	const { width, height } = useResizeObserver(container);
	const currentBounds = useMemo(() => graphBounds(positions), [positions]);
	const [bounds, setBounds] = useState(currentBounds);
	// Start at readable label size. Fit is an explicit overview, not the default.
	const [zoom, setZoom] = useState<number | undefined>(1);
	const drag = useRef<{ id: string; offset: GraphPosition } | null>(null);
	const arranging = !!onMove;
	useEffect(() => {
		// Freeze the canvas coordinate system throughout Arrange, so moving an
		// extremal node does not move the viewport under the pointer.
		if (!arranging) {
			setBounds(currentBounds);
		}
	}, [arranging, currentBounds]);
	const fit = Math.max(
		0.01,
		Math.min(
			1,
			Math.max(1, (width || 800) - 16) / bounds.width,
			Math.max(1, (height || 400) - 48) / bounds.height,
		),
	);
	const scale = zoom ?? fit;
	const point = (event: PointerEvent<SVGGElement>): GraphPosition | null => {
		const matrix = svg.current?.getScreenCTM();
		if (!matrix) {
			return null;
		}
		const value = new DOMPoint(event.clientX, event.clientY).matrixTransform(
			matrix.inverse(),
		);
		return { x: value.x, y: value.y };
	};
	const begin = (id: string, event: PointerEvent<SVGGElement>): void => {
		if (!onMove || event.button !== 0) {
			return;
		}
		const p = point(event);
		if (!p) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		event.currentTarget.setPointerCapture(event.pointerId);
		drag.current = {
			id,
			offset: { x: positions[id].x - p.x, y: positions[id].y - p.y },
		};
	};
	const move = (id: string, event: PointerEvent<SVGGElement>): void => {
		if (!onMove || drag.current?.id !== id) {
			return;
		}
		const p = point(event);
		if (p) {
			onMove(id, {
				x: p.x + drag.current.offset.x,
				y: p.y + drag.current.offset.y,
			});
		}
	};
	const end = (): void => {
		drag.current = null;
	};
	return { svg, container, bounds, scale, setZoom, begin, move, end };
}
